import moment from "moment-mini";
import Utils from "platform/util/Utils";
import {FormikObject} from "typings";
import {FieldType} from "enum/FieldType";
import Translations from "platform/translation/Translations";
import {TranslationKey} from "enum/TranslationKey";
import {PaymentMethod} from "enum/PaymentMethod";
import {APMClientDetails} from "protocol/request/DepositByAPMRequest";
import {ValidationError} from "core/util/Validation";
import {ClientDetail, PaymentMethodOrderItem, PaymentProvider} from "protocol/response/GetDepositConfigurationsResponse";
import {DepositByAPMResponse} from "protocol/request/DepositByAPMResponse";
import WebUtil from "platform/util/WebUtil";
import {FormMethod} from "platform/enum/FormMethod";
import {DeviceDetails} from "protocol/request/DeviceDetails";
import {PaymentMethodSortId} from "core/util/PaymentMethodSortId";

export class DepositUtil {

    public static formatCardExpiration(formik: FormikObject<any>): string {
        const expiration: string = formik.values[FieldType.CardExpiration];
        if (Utils.isNotEmpty(expiration)) {
            const parts: string[] = expiration.split("/");
            return `${parts[0] || Translations.text(TranslationKey.mm)}/${parts[1] || Translations.text(TranslationKey.yy)}`;
        }
        return Translations.text(TranslationKey.mmYY);
    }

    public static ccExpired(expiration: string): boolean {
        if (Utils.isNotNull(expiration)) {
            const parts: string[] = expiration.split("/");
            const iMonth: number = parseInt(parts[0], 10);
            const iYear: number = parseInt(parts[1], 10);
            const century = new Date().getFullYear().toString().substring(0, 2);
            const expDate: moment.Moment = moment(`${century}${iYear}` + "-" + (iMonth < 10 ? "0" : "") + iMonth, "YYYY-MM");
            const minMonth: number = new Date().getMonth() + 1;
            const minDate: moment.Moment = moment(new Date().getFullYear() + "-" + (minMonth < 10 ? "0" : "") + minMonth, "YYYY-MM");
            return expDate.isSame(minDate) ? false : !expDate.isAfter(minDate);
        }
        return false;
    }

    public static formatCvc = (cvc: string): string => {
        if (cvc) {
            let result: string = cvc;
            while (result.length < 3) {
                result += "•";
            }
            return result;
        }
        return "•••";
    }

    public static CCFlowType(paymentMethod: string | number): string {
        if (paymentMethod) {
            switch (paymentMethod) {
                case PaymentMethod.OtherCreditCard:
                    return "Other";
            }
        }
        return null;
    }

    public static CCIssuer(cardNumber: string): string {
        if (cardNumber) {
            if (/^(5019|4571)\d+$/.test(cardNumber)) {
                return "Dankort";
            } else if (/^(?:50|5[6-9]|6[0-9])\d+$/.test(cardNumber)) {
                return "Maestro";
            } else if (/^(?:5[1-5]|222[1-9]|22[3-9][0-9]|2[3-6][0-9][0-9]|27[0-1][0-9]|2720)\d+$/.test(cardNumber)) {
                return "MasterCard";
            } else if (/^(?:5[45]|36|30[0-5]|3095|3[8-9])\d+$/.test(cardNumber)) {
                return "Diners";
            } else if (/^(4026|4001|417500|4405|4508|4844|4913|4917)\d+$/.test(cardNumber)) {
                return "VisaElectron";
            } else if (/^4[0-9]\d+$/.test(cardNumber)) {
                return "Visa";
            } else if (/^(3(?:088|096|112|158|337|5(?:2[89]|[3-8][0-9]))\d{12})$/.test(cardNumber)) {
                return "JCB";
            } else if (/^3[47][0-9]{13}$/.test(cardNumber)) {
                return "American Express";
            }
        }
        return null;
    }

    public static BuildAPMClientDetails(Client: ClientDetail, form: {[key: string]: string}): APMClientDetails {
        const dateOfBirth: string = Utils.isNotEmpty(form[FieldType.DateOfBirth]) ? form[FieldType.DateOfBirth] : Client?.DateOfBirth;
        const formattedDateOfBirth: string = moment(dateOfBirth, "DD MM YYYY")?.format("DD-MMM-YYYY");

        return {
            IdNumber: form[FieldType.IDCard],
            BillAddress1: Utils.isNotEmpty(form[FieldType.Address]) ? form[FieldType.Address] : Client?.Address,
            BillCity: Utils.isNotEmpty(form[FieldType.City]) ? form[FieldType.City] : Client?.City,
            BillZipCode: Utils.isNotEmpty(form[FieldType.ZipCode]) ? form[FieldType.ZipCode] : Client?.ZipCode,
            BillCountryCode: Client?.CountryCode || form[FieldType.CountryCode],
            DateOfBirth: formattedDateOfBirth,
        };
    }

    public static BuildAPMTargetUrl(token: string, TransRefNum: string, QueryString: string, response: DepositByAPMResponse): string {
        const {GeneratedUrl, RequestForm} = response;
        let UseUrl: string = GeneratedUrl;
        let UseFormMethod: FormMethod;
        let UseFieldData: {Key: string, Value: string}[];
        if (RequestForm) {
            const {SubmitFormUrl, FormMethod, FieldData} = RequestForm;
            if (FormMethod && FieldData) {
                UseUrl = SubmitFormUrl;
                UseFormMethod = FormMethod;
                UseFieldData = FieldData;
            }
        }
        let TargetUrl = `https://${window.location.hostname}/SubmitForm.html?Token=${token}&TransRefNum=${TransRefNum}`
            + `&QueryString=${encodeURIComponent(QueryString)}&SubmitFormUrl=${encodeURIComponent(UseUrl)}`;
        if (Utils.isArrayNotEmpty(UseFieldData)) {
            const parameters: string[] = [];
            UseFieldData.forEach((field: {Key: string, Value: string}) => {
                parameters.push(`${field.Key}=${field.Value}`);
            });
            TargetUrl = WebUtil.addGetParams(TargetUrl, {
                FormMethod: UseFormMethod.toLowerCase(),
                FieldData: encodeURIComponent(parameters.join("(sep)"))
            });
        }
        return TargetUrl;
    }

    public static ActivityFormatValidationComment(errors: ValidationError[]): string {
        const fields: string[] = [];
        if (Utils.isArrayNotEmpty(errors)) {
            errors.forEach((error: ValidationError) => {
                fields.push(
                    Translations.TextEn(error.key, error.params)
                );
            });
        }
        return `Invalid Deposit fields: ${fields.join(", ")}`;
    }

    public static ApmId = (provider: PaymentProvider): string | number => {
        return Utils.isNotEmpty(provider?.ExternalPaymentProviderId) ? provider?.ExternalPaymentProviderId : provider?.EWalletId;
    }

    public static isApplePay = (provider: PaymentProvider): boolean => {
        return  provider?.EWalletId === 616;
    }

    public static isGooglePay = (provider: PaymentProvider): boolean => {
        return  provider?.EWalletId === 631;
    }

    public static isPayRetailer = (provider: PaymentProvider): boolean => {
        return  provider?.EWalletId === 618;
    }

    public static isBrite = (provider: PaymentProvider): boolean => {
        return  provider?.EWalletId === 623;
    }

    public static CustomPaymentOrder = (PaymentMethodOrder: PaymentMethodOrderItem[], method: PaymentMethodSortId): number => {
        if (method.Id === PaymentMethod.CreditCard || method.Id === PaymentMethod.OtherCreditCard) {
            return PaymentMethodOrder?.find(item => item.PaymentMethod === 2)?.Order;
        } else if (method.Id === PaymentMethod.WireTransfer) {
            return PaymentMethodOrder?.find(item => item.PaymentMethod === 6)?.Order;
        } else if (method.Id === PaymentMethod.PayRetailers) {
            return PaymentMethodOrder?.find(item => item.PspId === 618)?.Order;
        } else if (Utils.isNotNull(method?.EWalletAccountId)) {
            return PaymentMethodOrder?.find(item => item.PspAccountId === method?.EWalletAccountId)?.Order;
        }
        return 0;
    }

    public static GetDeviceData = (): DeviceDetails => {
        return {
            UserAgent: navigator?.userAgent,
            AcceptHeader: null,
            Language: navigator?.language,
            IpAddress: null,
            TimezoneOffset: new Date().getTimezoneOffset(),
            ColorDepth: window?.screen?.colorDepth?.toString(),
            PixelDepth: window?.screen?.pixelDepth?.toString(),
            PixelRatio: window?.devicePixelRatio?.toString(),
            ScreenHeight: window?.screen?.height,
            ScreenWidth: window?.screen?.width,
            ViewportHeight: window?.innerHeight,
            ViewportWidth: window?.innerWidth,
            JavaEnabled: false,
            JavascriptEnabled: true
        };
    }
}
